import { Vector2, Vector3, Plane, BoxGeometry, MeshBasicMaterial, Mesh } from 'three';
import { CSS3DObject } from 'three/examples/jsm/renderers/CSS3DRenderer.js';
import { VapiCamera } from '../vapi-UI/VapiCamera.js';
import { ReactiveComponent } from '../../core/ReactiveComponent.core.js';
import { computeProjectiveMatrix3d } from '../../plugins/3d-2d-transform/src/projectiveMatrix.js';
import { SCENE_INTERACTION } from '../../constants/scene.js';
import { vapiPerspectiveCornerHandleStyle } from '../../constants/vapiPerspectiveHandles.js';

/**
 * VRVapiCameraWithCSS3D - VAPI user camera (webcam during call).
 * Uses CSS3DObject in scene with an invisible 3D anchor mesh.
 * Drag updates anchor position; perspective is applied on camera DOM content.
 */
class VRVapiCameraWithCSS3D extends ReactiveComponent {
    constructor(params = {}) {
        super(null, {
            mode: params.editMode ? 'edit' : 'view'
        });

        const { model, camera, controls, raycaster, position, id, dbConfig, sizes } = params;
        this.id = id;
        this.model = model;
        this.camera = camera;
        this.controls = controls || null;
        this.position = position || { x: 0, y: 0, z: -900 };
        this.dbConfig = dbConfig || {};
        this._raycaster = raycaster || null;
        this._sizes = sizes || { width: window.innerWidth, height: window.innerHeight };
        this._viewportEl = params.viewportElement || document.querySelector('#webgl') || document.querySelector('#css3d');
        this._ndc = new Vector2();
        this._dragPlane = new Plane();
        this._dragPlaneNormal = new Vector3();
        this._planeIntersectPoint = new Vector3();
        this._dragGrabOffset = new Vector3();
        this._tmpVec = new Vector3();
        this._dragStartDistance = 0;
        this._dragOffsetX = 0;
        this._dragOffsetY = 0;
        this.isDragging = false;
        this._captureEl = null;
        this._isEditMode = !!params.editMode;
        this._css3dScale = 1;
        /** Same as VRVapiChairWithCSS3D: multiplies CSS3D plane scale without moving anchor distance. */
        this._userScale = Number(this.dbConfig?.size ?? this.dbConfig?.scale) || 1;
        this._rootTopOffset = '-20px';
        this._cornerHandles = [];
        this.parent = this;
        this.name = 'vapiUserCamera';
        this.type = 'vapiUserCamera';
        this.vraiAgentRoot = null;
        this._callCameraVisible = true;
        /** True while a VAPI webcam MediaStream is attached (during / after connect until cleared). */
        this._liveMediaAttached = false;
        this._commandText = String(this.dbConfig?.commandText || '').trim();
        /** Last anchor.visible state we pushed to the DOM (skip render() sync until it changes). */
        this._lastSyncedDomAnchorVisible = undefined;

        this._buildNode();
        this.anchor.position.set(
            this.position.x,
            this.position.y,
            this.position.z !== undefined ? this.position.z : -600
        );
        this.anchor.userData.vraiAgentCamera = this;

        if (this.state.mode === 'edit') {
            this._attachDragListeners();
        }
        this._setupVapiMediaListeners();
        this._setupUserAvatarFallbackListener();
        if (this.state.mode !== 'edit') {
            this._setupEndCallControl();
        }
        this._applyVisibilityFromConfig();
        return this;
    }

    _syncCameraEndCallUi() {
        const wrap = this.dom?.querySelector('.vrai-agent-camera-wrapper');
        if (!wrap) return;
        const chair = this._getOwningChair();
        const on = !!(chair && (chair._vapiCallActive || chair._vapiConnecting));
        wrap.classList.toggle('vrai-agent-call-running', on);
    }

    /**
     * End call control on the user camera panel (same .vrai-agent-chair-end-indicator as the agent chair).
     * Dispatches {@link SCENE_INTERACTION.END_VAPI_CALL}; {@link VRVapiChairWithCSS3D} handles confirm + stop.
     */
    _setupEndCallControl() {
        if (!this.model) return;
        const agentId = this.dbConfig?.vapiAgentId;
        if (!agentId) return;

        const wrap = this.dom?.querySelector('.vrai-agent-camera-wrapper');
        const endBtn = this.dom?.querySelector('.vrai-agent-camera-end-call');
        if (!wrap || !endBtn) return;

        this._onEndCallBtnClick = (e) => {
            e.stopPropagation();
            e.preventDefault();
            if (typeof this.model?.dispatchEvent !== 'function') return;
            this.model.dispatchEvent({
                type: SCENE_INTERACTION.END_VAPI_CALL,
                data: { vapiAgentId: agentId },
            });
        };
        endBtn.addEventListener('click', this._onEndCallBtnClick);

        this._onCameraWrapPointerEnter = () => wrap.classList.add('vrai-agent-camera-hovered');
        this._onCameraWrapPointerLeave = () => wrap.classList.remove('vrai-agent-camera-hovered');
        wrap.addEventListener('pointerenter', this._onCameraWrapPointerEnter);
        wrap.addEventListener('pointerleave', this._onCameraWrapPointerLeave);

        this._onVapiCallUiSyncForCamera = (e) => {
            const id = e?.data?.vapiAgentId;
            if (String(id) !== String(agentId)) return;
            this._syncCameraEndCallUi();
        };
        this.model.addEventListener('vapi-agent-call-start', this._onVapiCallUiSyncForCamera);
        this.model.addEventListener('vapi-agent-call-end', this._onVapiCallUiSyncForCamera);
        this.model.addEventListener('vapi-agent-status', this._onVapiCallUiSyncForCamera);

        this._syncCameraEndCallUi();
    }

    _getOwningChair() {
        const chairId = this.dbConfig?.vapiAgentId ?? null;
        const chairs = this.model?.view?.vraiAgentChairs || [];
        if (!chairId || !Array.isArray(chairs)) return null;
        return chairs.find(c => String(c.getId?.() ?? c.id) === String(chairId)) || null;
    }

    _getMenuDisplayName() {
        const t = typeof $trans !== 'undefined' && $trans('vrai-agent-camera');
        const componentName = t?.text || t || 'User Camera';
        const chair = this._getOwningChair();
        const ownerName = (chair?.dbConfig?.chairName || chair?.dbConfig?.name || '').trim();
        const ownName = (this.dbConfig?.name || '').trim();
        if (ownerName && ownName) return `${ownerName} - ${componentName}: ${ownName}`;
        if (ownerName) return `${ownerName} - ${componentName}`;
        return ownName ? `${componentName}: ${ownName}` : componentName;
    }

    _applyVisibilityFromConfig() {
        const inEditMode = !!this.model?.editMode || this.state.mode === 'edit';

        // If we can't resolve a chair yet, default to visible (avoid breaking editors/legacy scenes).
        const chair = this._getOwningChair();
        const chairCfg = chair?.dbConfig || {};

        const chairActive = chairCfg?.components?.chair?.active !== false;
        const cameraActive = chairCfg?.components?.camera?.active !== false;

        const ownHideOnStart = !!this.dbConfig?.hideOnStart;
        const chairHideOnStart = !!(chairCfg?.hideOnStart || chairCfg?.components?.chair?.hideOnStart);
        const hiddenByHideOnStart = !inEditMode && (ownHideOnStart || chairHideOnStart);

        // In view mode, the owning chair decides when the camera exists visually.
        const visible = inEditMode ? true : (chairActive && cameraActive && !hiddenByHideOnStart);

        if (this.anchor) this.anchor.visible = visible;
        this._syncDomVisibilityToAnchor();
        if (this.state.mode !== 'edit') {
            this._syncCameraEndCallUi();
        }
    }

    _syncDomVisibilityToAnchor() {
        if (!this.dom || !this.dom.style) return;
        const visible = this.anchor?.visible !== false;
        this.dom.classList.toggle('visible', visible);
        this.dom.classList.toggle('hide-on-start', !visible);
        this.dom.style.display = visible ? '' : 'none';
        // Camera component stays non-interactive in all modes/states.
        this.dom.style.pointerEvents = 'none';
        this._applyCallCameraVisibility();
        this._lastSyncedDomAnchorVisible = visible;
    }

    _getUserAvatarUrl() {
        const cfg = this.model?.config;
        if (!cfg) return './textures/defaultAvatar.png';
        let url = cfg.userAvatarUrl || '';
        if (!url) return './textures/defaultAvatar.png';
        if (/^(https?:|data:)/i.test(url) || url.startsWith('./') || url.startsWith('/')) return url;
        const base = cfg.baseWebUrl || '';
        return base + String(url).replace(/^\//, '');
    }

    _getCameraFallbackImageUrl() {
        const custom = String(this.dbConfig?.imageUrl || '').trim();
        if (custom) return custom;
        return this._getUserAvatarUrl();
    }

    /** When false (chair settings), hide avatar image when webcam is off or hidden; default true. */
    _shouldShowUserAvatarFallback() {
        const chair = this._getOwningChair();
        const camCfg = chair?.dbConfig?.components?.camera;
        return camCfg?.showUserAvatar !== false;
    }

    _applyCallCameraVisibility() {
        const wrapper = this.dom?.querySelector('.vrai-agent-camera-wrapper');
        if (!wrapper) return;
        const video = wrapper.querySelector('.vrai-webcam');
        const img = wrapper.querySelector('.vrai-webcam-avatar-fallback');
        const showAvatarImg = this._shouldShowUserAvatarFallback();
        wrapper.style.display = '';
        // Outside an active media session: hide <video>, show avatar until a new stream attaches.
        if (!this._liveMediaAttached) {
            if (video) video.style.display = 'none';
            if (img) {
                img.src = this._getCameraFallbackImageUrl();
                img.style.display = showAvatarImg ? 'block' : 'none';
            }
            return;
        }
        if (this._callCameraVisible) {
            if (video) video.style.display = '';
            if (img) img.style.display = 'none';
        } else {
            if (video) video.style.display = 'none';
            if (img) {
                img.src = this._getCameraFallbackImageUrl();
                img.style.display = showAvatarImg ? 'block' : 'none';
            }
        }
    }

    /** Re-apply webcam vs avatar UI after chair `components.camera` changes (e.g. Show user avatar toggle). */
    refreshUserAvatarFallback() {
        this._applyCallCameraVisibility();
    }

    setCallCameraVisible(visible) {
        this._callCameraVisible = visible !== false;
        this._applyCallCameraVisibility();
    }

    dispatchCommand(commandText = null) {
        const text = String(commandText ?? this.dbConfig?.commandText ?? this._commandText ?? '').trim();
        if (!text) return false;
        const chair = this._getOwningChair();
        if (chair?.dispatchCommand) return chair.dispatchCommand(text);
        return false;
    }

    _setupUserAvatarFallbackListener() {
        if (!this.model) return;
        this._onUserAvatarChanged = () => {
            if (!this._callCameraVisible || !this._liveMediaAttached) this._applyCallCameraVisibility();
        };
        this.model.addEventListener(SCENE_INTERACTION.USER_AVATAR_CHANGED, this._onUserAvatarChanged);
    }

    _buildNode() {
        const w = 480;
        const h = 360;
        const vapiCameraInstance = new VapiCamera({ width: w, height: h, userName: '' });
        const cameraContent = vapiCameraInstance.render();
        cameraContent.style.width = '100%';
        cameraContent.style.height = '100%';
        if (this._isEditMode) {
            cameraContent.style.pointerEvents = 'none';
        }

        const root = document.createElement('div');
        root.setAttribute('class', 'vrai-agent-component video_element vapi_camera2_wrapper vapi-camera2d-indicator');
        root.setAttribute('data-css3d-interactive', 'true');
        root.setAttribute('data-id', `vapi_camera2_${this.id}`);
        root.style.cssText = `
            position: relative;
            width: ${w}px;
            height: ${h}px;
            pointer-events: ${this._isEditMode ? 'auto' : 'none'};
            transform-style: preserve-3d;
            backface-visibility: hidden;
            -webkit-backface-visibility: hidden;
            will-change: transform;
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale;
            text-rendering: geometricPrecision;
            user-select: none;
            -webkit-user-select: none;
        `;
        root.style.opacity = this.state.mode === 'edit' ? '0.9' : '1';
        root.classList.add(`vapi-camera2d-${this.id}`);

        root.addEventListener('pointerdown', (e) => {
            if (e.button !== 0) return;
            // In view mode we ignore all pointer interaction on the camera component.
            if (this.state.mode !== 'edit') return;
            if (this._onPointerDown) {
                if (!e.target?.closest?.('.vapi-camera2-perspective-content')) return;
                e.preventDefault();
                e.stopPropagation();
                this._onPointerDown(e);
            }
        });

        const originalPos = [{ x: 0, y: 0 }, { x: w, y: 0 }, { x: w, y: h }, { x: 0, y: h }];
        const saved = this.dbConfig?.perspectiveOffsets;
        this._perspectiveCorners = Array.isArray(saved) && saved.length === 4
            ? saved.map(p => ({ x: Number(p.x) ?? p.x, y: Number(p.y) ?? p.y }))
            : originalPos.map(p => ({ ...p }));

        const perspectiveContent = document.createElement('div');
        perspectiveContent.className = 'vapi-camera2-perspective-content';
        perspectiveContent.style.cssText = 'position:absolute;inset:0;overflow:visible;transform-origin:0 0;transform-style:preserve-3d;backface-visibility:hidden;-webkit-backface-visibility:hidden;will-change:transform;pointer-events:auto;';
        if (this._isEditMode) perspectiveContent.style.cursor = 'grab';
        perspectiveContent.appendChild(cameraContent);
        this._perspectiveContent = perspectiveContent;

        const perspectiveWrapper = document.createElement('div');
        perspectiveWrapper.className = 'vapi-camera2-perspective-wrapper';
        perspectiveWrapper.style.cssText = 'position:absolute;inset:0;overflow:visible;';
        perspectiveWrapper.appendChild(perspectiveContent);

        this._applyPerspectiveTransform = () => {
            const m = computeProjectiveMatrix3d(originalPos, this._perspectiveCorners);
            perspectiveContent.style.transform = m;
            root.style.setProperty('top', this.state.mode === 'edit' ? '0px' : this._rootTopOffset, 'important');
        };
        this._applyPerspectiveTransform();

        if (this.state.mode === 'edit') {
            for (let i = 0; i < 4; i++) {
                const hnd = document.createElement('div');
                hnd.className = 'vapi-camera2-corner-handle';
                hnd.dataset.corner = String(i);
                hnd.style.cssText = vapiPerspectiveCornerHandleStyle('pointer-events:auto;');
                const updateHandlePos = () => { hnd.style.left = this._perspectiveCorners[i].x + 'px'; hnd.style.top = this._perspectiveCorners[i].y + 'px'; };
                updateHandlePos();
                const onCornerPointerDown = (e) => {
                    if (this.dbConfig?.lockedInEdit) return;
                    e.preventDefault();
                    e.stopPropagation();
                    hnd.style.cursor = 'grabbing';
                    const startX = e.clientX, startY = e.clientY;
                    const startPos = { x: this._perspectiveCorners[i].x, y: this._perspectiveCorners[i].y };
                    const onMove = (ev) => {
                        const dx = ev.clientX - startX, dy = ev.clientY - startY;
                        this._perspectiveCorners[i].x = startPos.x + dx;
                        this._perspectiveCorners[i].y = startPos.y + dy;
                        updateHandlePos();
                        this._applyPerspectiveTransform();
                    };
                    const onUp = () => {
                        hnd.style.cursor = 'grab';
                        document.removeEventListener('pointermove', onMove);
                        document.removeEventListener('pointerup', onUp);
                        this.dbConfig = { ...this.dbConfig, perspectiveOffsets: this._perspectiveCorners.map(p => ({ x: p.x, y: p.y })) };
                        if (this.model?.view?.updateJSON) this.model.view.updateJSON(this.anchor, null);
                    };
                    document.addEventListener('pointermove', onMove);
                    document.addEventListener('pointerup', onUp);
                };
                hnd.addEventListener('pointerdown', onCornerPointerDown);
                perspectiveWrapper.appendChild(hnd);
                this._cornerHandles.push(hnd);
            }
        }

        root.appendChild(perspectiveWrapper);

        this.root = root;
        this.dom = root;
        this.css3dObject = new CSS3DObject(root);
        const initialScale = (this._css3dScale = this._getViewportScale()) * (this._userScale ?? 1);
        this.css3dObject.scale.set(initialScale, initialScale, 1);

        const geo = new BoxGeometry(0.1, 0.1, 0.1);
        const mat = new MeshBasicMaterial({ visible: false });
        this.anchor = new Mesh(geo, mat);
        this.anchor.name = 'vapiUserCamera';
        this.anchor.type = 'vapiUserCamera';
        this.anchor.groupDetails = {
            title: (typeof $trans !== 'undefined' && $trans('vrai-agent-camera')) || 'User Camera',
            icon: 'videocam',
            editTitle: (typeof $trans !== 'undefined' && $trans('edit-vrai-agent-camera')) || 'Edit User Camera',
            dataLang: 'edit-vrai-agent-camera'
        };
        this.anchor.add(this.css3dObject);

        this.elementClass = `vapi-camera2d-${this.id}`;
        this.mesh = this.anchor;
        this.object = this.anchor;
    }

    _setupVapiMediaListeners() {
        this._vapiAgentId = this.dbConfig?.vapiAgentId || null;

        if (this.model && this._vapiAgentId) {
            this._getWebcamVideo = () => {
                const fromRoot = this.dom?.querySelector?.('.vrai-webcam');
                if (fromRoot && document.contains(fromRoot)) return fromRoot;
                const fromDoc = document.querySelector(`[data-id="vapi_camera2_${this.id}"] .vrai-webcam`);
                return fromDoc || null;
            };
            this._handleVapiMedia = (e) => {
                const d = (e && e.data) || {};
                if (d.vapiAgentId !== this._vapiAgentId) return;
                const video = this._getWebcamVideo();
                if (!d.stream) {
                    this._liveMediaAttached = false;
                    if (video?.srcObject) {
                        try {
                            const tracks = video.srcObject.getTracks?.() || [];
                            tracks.forEach(t => t.stop?.());
                            video.srcObject = null;
                        } catch (err) { }
                    }
                    if (video) video.style.display = 'none';
                    this._applyCallCameraVisibility();
                    return;
                }
                this._liveMediaAttached = true;
                if (!video) return;
                try {
                    video.srcObject = d.stream;
                    this._applyCallCameraVisibility();
                } catch (err) {
                    console.warn('[VAPI] failed to attach media stream', err);
                }
            };
            this._handleVapiCallEnd = (e) => {
                const d = (e && e.data) || {};
                if (d.vapiAgentId !== this._vapiAgentId) return;
                const chair = this._getOwningChair();
                const cameraVisible = !(chair?._isCameraHidden);
                this.setCallCameraVisible(cameraVisible);
                // Stream is usually cleared via vapi-agent-media { stream: null } before this; ensure idle UI.
                this._liveMediaAttached = false;
                const video = this._getWebcamVideo();
                if (video?.srcObject) {
                    try {
                        const tracks = video.srcObject.getTracks?.() || [];
                        tracks.forEach(t => t.stop?.());
                        video.srcObject = null;
                    } catch (err) { }
                }
                this._applyCallCameraVisibility();
            };
            this.model.addEventListener('vapi-agent-media', this._handleVapiMedia);
            this.model.addEventListener('vapi-agent-call-end', this._handleVapiCallEnd);
        }
    }

    _getViewportScale() {
        const vpEl = this._viewportEl;
        const canvasH = vpEl?.clientHeight || 0;
        const winH = window.innerHeight;
        return (canvasH > 0 && winH > canvasH) ? winH / canvasH : 1;
    }

    /**
     * Keep perspective corner handles a stable on-screen size when the CSS3D plane is scaled.
     */
    _syncCornerHandleInverseScale() {
        if (!this.css3dObject) return;
        const s = Math.max(this.css3dObject.scale.x || 1, 0.02);
        const inv = 1 / s;
        const t = `translate(-50%,-50%) scale(${inv})`;
        if (this._cornerHandles?.length) {
            for (const hnd of this._cornerHandles) {
                hnd.style.transform = t;
            }
        }
    }

    _clientToNDC(clientX, clientY) {
        const el = this._viewportEl;
        if (!el) {
            this._ndc.x = (clientX / this._sizes.width) * 2 - 1;
            this._ndc.y = -(clientY / this._sizes.height) * 2 + 1;
            return;
        }
        const rect = el.getBoundingClientRect();
        this._ndc.x = ((clientX - rect.left) / rect.width) * 2 - 1;
        this._ndc.y = 1 - ((clientY - rect.top) / rect.height) * 2;
    }

    _setFromClientXY(clientX, clientY) {
        this._clientToNDC(clientX, clientY);
        this._raycaster.setFromCamera(this._ndc, this.camera);
        if (this._raycaster.ray.intersectPlane(this._dragPlane, this._planeIntersectPoint)) {
            // Keep component at constant camera distance while dragging.
            this._tmpVec.copy(this._planeIntersectPoint).add(this._dragGrabOffset);
            this._tmpVec.sub(this.camera.position).normalize();
            this._tmpVec.multiplyScalar(this._dragStartDistance).add(this.camera.position);
            if (this.anchor.parent) {
                this.anchor.parent.updateMatrixWorld(true);
                this.anchor.parent.worldToLocal(this._tmpVec);
                this.anchor.position.copy(this._tmpVec);
            } else {
                this.anchor.position.copy(this._tmpVec);
            }
        }
    }

    _onPointerMove = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!this.isDragging) return;
        this._setFromClientXY(e.clientX, e.clientY);
        this.anchor.lookAt(this.camera.position);
    };

    _onPointerUp = (e) => {
        if (!this.isDragging) return;
        this.isDragging = false;
        if (this._perspectiveContent) this._perspectiveContent.style.cursor = 'grab';
        if (this._captureEl) {
            try { this._captureEl.releasePointerCapture(e.pointerId); } catch (err) { }
            this._captureEl = null;
        }
        window.removeEventListener('pointermove', this._onPointerMove);
        window.removeEventListener('pointerup', this._onPointerUp);
        window.removeEventListener('pointercancel', this._onPointerUp);
        if (this.controls) this.controls.enabled = true;
        if (this.model?.view) {
            if (this.model.view.updateJSON) this.model.view.updateJSON(this.anchor, null);
            this.model.view.elementSelected = this;
            this.showObjectMenu();
            if (typeof $ !== 'undefined') $('.edit_element').trigger('click');
        }
    };

    _onPointerDown = (e) => {
        if (this.state.mode !== 'edit') return;
        if (this.dbConfig?.lockedInEdit) return;
        if (this.isDragging) return;
        if (e.target?.closest?.('input, textarea, button, a')) return;
        if (e.target?.closest?.('.vapi-camera2-corner-handle')) return;
        this._dragOffsetX = 0;
        this._dragOffsetY = 0;
        this.isDragging = true;
        this._captureEl = e.currentTarget || this.dom;
        try { this._captureEl?.setPointerCapture?.(e.pointerId); } catch (err) { }
        if (this.anchor.parent) {
            this.anchor.parent.updateMatrixWorld(true);
            this.anchor.getWorldPosition(this._tmpVec);
        } else {
            this._tmpVec.copy(this.anchor.position);
        }
        this._dragPlaneNormal.subVectors(this._tmpVec, this.camera.position).normalize();
        this._dragPlane.setFromNormalAndCoplanarPoint(this._dragPlaneNormal, this._tmpVec);
        this._dragStartDistance = this.camera.position.distanceTo(this._tmpVec);
        this._clientToNDC(e.clientX, e.clientY);
        this._raycaster.setFromCamera(this._ndc, this.camera);
        if (this._raycaster.ray.intersectPlane(this._dragPlane, this._planeIntersectPoint)) {
            this._dragGrabOffset.copy(this._tmpVec).sub(this._planeIntersectPoint);
        } else {
            this._dragGrabOffset.set(0, 0, 0);
        }
        if (this._perspectiveContent) this._perspectiveContent.style.cursor = 'grabbing';
        window.addEventListener('pointermove', this._onPointerMove);
        window.addEventListener('pointerup', this._onPointerUp);
        window.addEventListener('pointercancel', this._onPointerUp);
        if (this.controls) this.controls.enabled = false;
    };

    _attachDragListeners() {
        if (this._perspectiveContent) this._perspectiveContent.style.cursor = 'grab';
    }

    _detachDragListeners() {
        if (this._perspectiveContent) this._perspectiveContent.style.cursor = '';
        window.removeEventListener('pointermove', this._onPointerMove);
        window.removeEventListener('pointerup', this._onPointerUp);
        window.removeEventListener('pointercancel', this._onPointerUp);
        this.isDragging = false;
        if (this.controls) this.controls.enabled = true;
    }

    render(camera) {
        if (!camera) return;
        if (!this.isDragging) {
            this.anchor.lookAt(camera.position);
            this._css3dScale = this._getViewportScale();
            const scale = this._css3dScale * (this._userScale ?? 1);
            if (this.css3dObject) this.css3dObject.scale.set(scale, scale, 1);
        }
        this._syncCornerHandleInverseScale();
        // DOM/video/avatar state is updated from config, VAPI events, and setCallCameraVisible — not every frame.
        const anchorVis = this.anchor?.visible !== false;
        if (this._lastSyncedDomAnchorVisible === anchorVis) return;
        this._syncDomVisibilityToAnchor();
    }

    getId() {
        return this.vraiAgentRoot ? this.vraiAgentRoot.getId() : this.id;
    }

    getDbConfig() {
        return this.vraiAgentRoot ? this.vraiAgentRoot.getDbConfig() : this.dbConfig;
    }

    setDbConfig(config) {
        this.dbConfig = { ...this.dbConfig, ...config };
        this._vapiAgentId = this.dbConfig?.vapiAgentId ?? this._vapiAgentId;
        this._commandText = String(this.dbConfig?.commandText || '').trim();
        if (config?.size !== undefined || config?.scale !== undefined) {
            const s = config.size ?? config.scale;
            this._userScale = Number(s) > 0 ? Number(s) : 1;
        }
        if (config?.perspectiveOffsets && Array.isArray(config.perspectiveOffsets) && this._perspectiveCorners) {
            const w = this.root?.offsetWidth || 480;
            const h = this.root?.offsetHeight || 360;
            this._perspectiveCorners.splice(0, 4, ...config.perspectiveOffsets.map(p => (p.xr !== undefined && p.yr !== undefined)
                ? { x: Number(p.xr) * w, y: Number(p.yr) * h }
                : { x: Number(p.x) ?? p.x, y: Number(p.y) ?? p.y }));
            this._syncHandlePositions?.();
            this._applyPerspectiveTransform?.();
        }
        this._applyVisibilityFromConfig();
        return this.vraiAgentRoot ? this.vraiAgentRoot.setDbConfig(config) : null;
    }

    getLimits() {
        return this.vraiAgentRoot ? this.vraiAgentRoot.getLimits() : { min: 280, max: 800, distance: 540 };
    }

    getAxisRotations() {
        return this.vraiAgentRoot ? this.vraiAgentRoot.getAxisRotations() : { rotateX: 0, rotateY: 0 };
    }

    setDistance(val) {
        return this.vraiAgentRoot ? this.vraiAgentRoot.setDistance(val) : null;
    }

    /** Visual scale of the camera plane (does not move the anchor). Persisted as dbConfig.size. */
    setSize(val) {
        const n = Number(val);
        if (!Number.isFinite(n) || n <= 0) return;
        this._userScale = n;
        this.dbConfig = { ...this.dbConfig, size: n };
    }

    setAxisRotations(axis, angle) {
        if (this.vraiAgentRoot) this.vraiAgentRoot.setAxisRotations(axis, angle);
    }

    showObjectMenu() {
        $('.object-menu .object-name').text(this._getMenuDisplayName());
        $('.object-menu').addClass('is-active');
        $('.object-menu').addClass('edit');
        $('.vrai-agent-settings').addClass('is-active');
        $('.perspective-settings:not(.vrai-agent-perspective), .tooltip-settings, .video-loop, .iframe-agent-settings').addClass('hidden');
    }

    toggleSelect() {
        this.dom?.querySelector('.vrai-agent-camera-wrapper')?.classList?.toggle('active');
    }

    _perspectiveOffsetsForDb() {
        const w = this.root?.offsetWidth || 480;
        const h = this.root?.offsetHeight || 360;
        return (this._perspectiveCorners || []).map(p => ({ x: p.x, y: p.y, xr: p.x / w, yr: p.y / h }));
    }

    _syncHandlePositions() {
        const handles = this.dom?.querySelectorAll('.vapi-camera2-corner-handle');
        handles?.forEach((h, i) => {
            if (this._perspectiveCorners[i]) {
                h.style.left = this._perspectiveCorners[i].x + 'px';
                h.style.top = this._perspectiveCorners[i].y + 'px';
            }
        });
    }

    _resetPerspectiveToRect() {
        if (!this._perspectiveCorners) return;
        const w = this.root?.offsetWidth || 480;
        const h = this.root?.offsetHeight || 360;
        const originalPos = [{ x: 0, y: 0 }, { x: w, y: 0 }, { x: w, y: h }, { x: 0, y: h }];
        this._perspectiveCorners.splice(0, 4, ...originalPos.map(p => ({ ...p })));
        this._syncHandlePositions();
        this._applyPerspectiveTransform?.();
        this.dbConfig = { ...this.dbConfig, perspectiveOffsets: this._perspectiveOffsetsForDb() };
    }

    resetPerspective() {
        this._resetPerspectiveToRect();
    }

    disposeCss3D() {
        if (this.model && this._onUserAvatarChanged) {
            this.model.removeEventListener(SCENE_INTERACTION.USER_AVATAR_CHANGED, this._onUserAvatarChanged);
            this._onUserAvatarChanged = null;
        }
        if (this.model && this._onVapiCallUiSyncForCamera) {
            this.model.removeEventListener('vapi-agent-call-start', this._onVapiCallUiSyncForCamera);
            this.model.removeEventListener('vapi-agent-call-end', this._onVapiCallUiSyncForCamera);
            this.model.removeEventListener('vapi-agent-status', this._onVapiCallUiSyncForCamera);
            this._onVapiCallUiSyncForCamera = null;
        }
        const wrap = this.dom?.querySelector('.vrai-agent-camera-wrapper');
        const endBtn = this.dom?.querySelector('.vrai-agent-camera-end-call');
        if (endBtn && this._onEndCallBtnClick) {
            endBtn.removeEventListener('click', this._onEndCallBtnClick);
            this._onEndCallBtnClick = null;
        }
        if (wrap && this._onCameraWrapPointerEnter) {
            wrap.removeEventListener('pointerenter', this._onCameraWrapPointerEnter);
            wrap.removeEventListener('pointerleave', this._onCameraWrapPointerLeave);
            this._onCameraWrapPointerEnter = null;
            this._onCameraWrapPointerLeave = null;
        }
        if (this.model && this._handleVapiMedia && this._handleVapiCallEnd) {
            this.model.removeEventListener('vapi-agent-media', this._handleVapiMedia);
            this.model.removeEventListener('vapi-agent-call-end', this._handleVapiCallEnd);
            this._handleVapiMedia = null;
            this._handleVapiCallEnd = null;
        }
        if (this._webcamVideo?.srcObject) {
            try {
                (this._webcamVideo.srcObject.getTracks?.() || []).forEach(t => t.stop?.());
            } catch (e) { }
            this._webcamVideo.srcObject = null;
        }
        this._webcamVideo = null;
    }

    destroy() {
        this.disposeCss3D();
        try { this._detachDragListeners(); } catch (e) { }
        if (this.dom?.parentNode) this.dom.parentNode.removeChild(this.dom);
    }

    /** Returns the poster div for use as a sub-component (e.g. VRVraiAgent Ghost). */
    static getPosterElement(model, w = 480, h = 360) {
        const vapiCameraInstance = new VapiCamera({ width: w, height: h });
        const wrapDiv = vapiCameraInstance.render();
        wrapDiv.setAttribute('class', (wrapDiv.getAttribute('class') || '') + ' vrai-agent-component video_element');
        wrapDiv.style.width = `${w}px`;
        wrapDiv.style.height = `${h}px`;
        wrapDiv.style.position = 'relative';
        wrapDiv.style.pointerEvents = model?.editMode ? 'none' : 'auto';
        return wrapDiv;
    }
}

export { VRVapiCameraWithCSS3D };
